import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/tiptap/styles.css";
import App from "./App";
import "./index.css";
import { Amplify } from "aws-amplify";
import { MICROSERVICE_URLS } from "./config/api";
import { sentryBeforeSend } from "./utils/sentryBeforeSend";

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  sendDefaultPii: false,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Only attach sentry-trace headers to our own microservices — not Stripe, Cognito, Pusher, etc.
  tracePropagationTargets: Object.values(MICROSERVICE_URLS).filter(Boolean),
  tracesSampleRate:
    process.env.REACT_APP_SENTRY_ENVIRONMENT === "production" ? 0.2 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
  enabled: Boolean(process.env.REACT_APP_SENTRY_DSN),
  // NOTE: these match on the raw error message/value. The auth resilience
  // logging in AuthContext / AppContext deliberately uses custom
  // captureMessage names ("auth.session_reset",
  // "auth.checkauth_transient_kept_session", "auth.token_refresh_failed",
  // "auth.session_expired_logout") that do NOT match any entry below, so those
  // signals are never dropped here.
  ignoreErrors: [
    /ResizeObserver loop/,
    "Failed to fetch",
    "Load failed",
    "NetworkError",
    "Network request failed",
    "NotAuthorizedException",
    /ChunkLoadError/,
    /AbortError/,
  ],
  beforeSend: sentryBeforeSend,
});

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
      signUpVerificationMethod: "code",
    },
  },
});

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
